import { Checklist, ChecklistCriteria } from "types/Checklist";
import { Revision } from "types/Revision";
import { RevisionAnnotations } from "types/RevisionAnnotation";
import { SubmittedDocument } from "types/SubmittedDocument";
import { WordChecklistCells } from "types/WordChecklist";
import { RevisionCommentPlacement } from "~/types/enums/RevisionCommentPlacement.enum";
import {
  ExportBlock,
  ExportInlineRun,
  useQuillOpsToExportBlocks,
} from "../quill-export/useQuillOpsToExportBlocks";
import { usePDFChecklistGenerator } from "./usePDFChecklistGenerator";

const normalizeLineHeight = (lineHeight?: number) => {
  if (!Number.isFinite(lineHeight)) return 1;
  return Math.max(1, Number(lineHeight));
};

const toPointValue = (raw?: string) => {
  if (!raw) return 0;

  const normalized = String(raw).trim().toLowerCase();
  if (normalized.endsWith("em")) {
    const value = Number(normalized.replace("em", ""));
    return Number.isFinite(value) ? value * 12 : 0;
  }

  if (normalized.endsWith("pt")) {
    const value = Number(normalized.replace("pt", ""));
    return Number.isFinite(value) ? value : 0;
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
};

export const getPDFOpsBlockIndent = (block: ExportBlock) => {
  const indentLevel = Math.max(0, block.indentLevel ?? 0);
  const leftIndent = block.leftIndentWidth != undefined
    ? toPointValue(block.leftIndentWidth)
    : indentLevel * 18;
  const hangingIndentPt = block.hasHangingIndent ? 24 : 0;
  const firstLineIndentPt =
    toPointValue(block.firstLineIndentWidth) +
    toPointValue(block.leadingFirstLineIndentWidth);

  return {
    paragraphLeftMargin: leftIndent + hangingIndentPt,
    leadingIndent: firstLineIndentPt - hangingIndentPt,
  };
};

const toPDFAlignment = (align?: string) => {
  switch (align) {
    case "center":
      return "center";
    case "right":
      return "right";
    case "justify":
      return "justify";
    default:
      return "left";
  }
};

const normalizeInlineText = (text: string) => {
  if (!text) return "";

  // Match editor tab-stop intent: tab-size is effectively 2em.
  // Keep ordinary spaces breakable, including standalone review-marking runs.
  // Paragraph preserveLeadingSpaces already preserves intentional leading space.
  return text.replace(/\t/g, "\u2003\u2003");
};

const normalizePDFBackground = (background?: string) => {
  if (!background) return undefined;

  return background.trim().toLowerCase() === "transparent"
    ? undefined
    : background;
};

export const convertInlineRunsToPDFText = (runs: ExportInlineRun[]) => {
  return runs.map((run) => {
    const normalizedText = normalizeInlineText(run.text);
    const decorations = [];

    if (run.underline) decorations.push("underline");
    if (run.strike) decorations.push("lineThrough");

    const result: any = {
      text: normalizedText,
      bold: run.bold || false,
      italics: run.italic || false,
      decoration:
        decorations.length > 1 ? decorations : decorations[0],
      color: run.color,
      background: normalizePDFBackground(run.background),
      link: run.link,
      fontSize: run.fontSizePt,
    };

    if (run.script === "sub") {
      result.baseline = -2;
      result.fontSize = Math.max(8, (run.fontSizePt ?? 12) - 2);
    } else if (run.script === "super") {
      result.baseline = 4;
      result.fontSize = Math.max(8, (run.fontSizePt ?? 12) - 2);
    }

    return result;
  });
};

const buildHeader = (revision: Revision, getCompositionStartPage: () => number) => {
  const header = revision.headers?.[0];
  if (!header) return undefined;

  const extraText = header.extraText?.trim();
  const shouldShowPageNumber = header.includePageNumbers === true;

  if (!extraText && !shouldShowPageNumber) return undefined;

  return (currentPage: number) => {
    const compositionStartPage = getCompositionStartPage();
    if (currentPage < compositionStartPage) return undefined;

    const textParts = [];
    if (extraText) textParts.push(extraText);
    if (shouldShowPageNumber) {
      textParts.push(String(currentPage - compositionStartPage + 1));
    }

    return {
      margin: [60, 24, 60, 0],
      text: textParts.join(" "),
      fontSize: 10,
      alignment: toPDFAlignment(header.alignment),
    };
  };
};

const appendCommentsToPDFContent = (
  content: any[],
  revision: Revision,
  commentColor: number[]
) => {
  if (!revision.commentObject) return;

  const commentLines = useConvertQuillDeltaToPDFRun(
    revision.commentObject,
    12,
    useRGBToHex(commentColor)
  );

  if (commentLines.length > 0) {
    content.push({ text: "\n" });
  }

  for (const line of commentLines) {
    content.push({ text: line });
  }
};

const appendAnnotationSummary = (
  content: any[],
  annotations: RevisionAnnotations,
  commentColor: number[]
) => {
  if (!Array.isArray(annotations) || annotations.length === 0) return;

  const enabled = annotations
    .filter((annotation) => annotation?.isEnabled !== false)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  if (enabled.length === 0) return;

  content.push({ text: "\n" });
  content.push({ text: "Teacher Annotations", bold: true, fontSize: 12 });

  let annotationIndex = 1;
  for (const annotation of enabled) {
    const runs = useConvertQuillDeltaToPDFRun(
      annotation.commentObject ?? [],
      11,
      useRGBToHex(commentColor)
    )
      .flat()
      .filter((run) => run.text != undefined && run.text !== "");

    if (runs.length === 0) continue;

    content.push({
      margin: [0, 2, 0, 0],
      text: [{ text: `${annotationIndex}. `, bold: true }, ...runs],
    });
    annotationIndex++;
  }
};

const imageSrcToDataUrl = async (src: string) => {
  const response = await fetch(src);
  const blob = await response.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      if (!reader.result || typeof reader.result !== "string") {
        reject(new Error("Unable to load image data"));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });
};

const appendOpsBlocksToPDFContent = async (content: any[], blocks: ExportBlock[]) => {
  const orderedCounters: number[] = [];

  for (const block of blocks) {
    if (block.type === "page-break") {
      content.push({ text: "", pageBreak: "before" });
      continue;
    }

    if (block.type === "image" && block.imageSrc) {
      try {
        const imageData = await imageSrcToDataUrl(block.imageSrc);
        content.push({
          image: imageData,
          alignment: toPDFAlignment(block.align),
          width: 420,
          margin: [0, 4, 0, 4],
        });
      } catch (error) {
        // Keep export resilient when an image cannot be fetched.
      }
      continue;
    }

    const runs = convertInlineRunsToPDFText(block.runs ?? []);
    const alignment = toPDFAlignment(block.align);
    const lineHeight = normalizeLineHeight(block.lineHeight);
    const indentLevel = Math.max(0, block.indentLevel ?? 0);
    const leftIndent = block.leftIndentWidth != undefined
      ? toPointValue(block.leftIndentWidth)
      : indentLevel * 18;
    const { paragraphLeftMargin, leadingIndent } =
      getPDFOpsBlockIndent(block);

    const baseParagraph: any = {
      text: runs.length > 0 ? runs : [{ text: "" }],
      alignment,
      lineHeight,
      margin: [paragraphLeftMargin, 0, 0, 0],
      // Keep intentional leading spacing (tabs/indents) from Quill text runs.
      preserveLeadingSpaces: true,
      leadingIndent,
    };

    if (block.isCodeBlock) {
      baseParagraph.font = "Roboto";
      baseParagraph.fontSize = 10;
      baseParagraph.background = "#F5F5F5";
      baseParagraph.margin = [paragraphLeftMargin + 8, 2, 0, 2];
    }

    if (block.isBlockquote) {
      baseParagraph.margin = [leftIndent + 16, 0, 0, 0];
      baseParagraph.color = "#444444";
    }

    if (block.headerLevel != undefined && block.headerLevel > 0) {
      baseParagraph.bold = true;
      baseParagraph.fontSize = Math.max(12, 20 - block.headerLevel * 2);
      baseParagraph.margin = [leftIndent, 4, 0, 2];
    }

    if (block.listType === "ordered") {
      const level = indentLevel;
      if (orderedCounters[level] == undefined) {
        orderedCounters[level] = 0;
      }
      orderedCounters[level]++;

      for (let i = level + 1; i < orderedCounters.length; i++) {
        orderedCounters[i] = 0;
      }

      content.push({
        ...baseParagraph,
        text: [
          { text: `${orderedCounters[level]}. `, bold: true },
          ...(Array.isArray(baseParagraph.text) ? baseParagraph.text : []),
        ],
        margin: [leftIndent, 0, 0, 0],
      });
      continue;
    }

    if (block.listType === "bullet") {
      content.push({
        ...baseParagraph,
        text: [
          { text: "\u2022 ", bold: true },
          ...(Array.isArray(baseParagraph.text) ? baseParagraph.text : []),
        ],
        margin: [leftIndent, 0, 0, 0],
      });
      continue;
    }

    orderedCounters.length = 0;
    content.push(baseParagraph);
  }
};

export const usePDFOpsGenerator = () => {
  const exportPDFOpsDocument = async (
    submittedDocument: SubmittedDocument,
    revision: Revision,
    checklist: Checklist | undefined,
    allCriteria: ChecklistCriteria[] | undefined,
    wordChecklistCells: WordChecklistCells | undefined,
    annotations: RevisionAnnotations,
    totalEarnedScore: number | undefined,
    earnedScore: number | undefined,
    commentColor: number[],
    returnBlob: boolean = false,
    printMode: boolean = false,
    hideScores: boolean = false,
    fileNameOverride: string | undefined = undefined,
    includeCriteriaCheckmarks: boolean = false,
    isStudentMode: boolean = false
  ): Promise<Blob | undefined> => {
    let docName = fileNameOverride ?? submittedDocument.name;
    docName = docName.replace(".docx", "");
    if (fileNameOverride == undefined) {
      docName = `${docName}_graded.pdf`;
    } else {
      docName = `${docName}.pdf`;
    }

    const content: any[] = [];

    if (
      checklist &&
      wordChecklistCells &&
      allCriteria &&
      totalEarnedScore != undefined &&
      earnedScore != undefined
    ) {
      const checklistData = usePDFChecklistGenerator(
        submittedDocument,
        checklist,
        wordChecklistCells,
        allCriteria,
        totalEarnedScore,
        earnedScore,
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode
      );
      content.push(...checklistData);
    }

    const composition: {
      stack: any[];
      pageBreak?: "before";
      positions?: { pageNumber: number }[];
    } = {
      stack: [],
      pageBreak: content.length > 0 ? "before" : undefined,
    };
    content.push(composition);

    const revisionCommentPlacement =
      revision.commentPlacement ?? RevisionCommentPlacement.AfterDocument;

    if (revisionCommentPlacement == RevisionCommentPlacement.BeforeDocument) {
      appendCommentsToPDFContent(composition.stack, revision, commentColor);
    }

    const blocks = useQuillOpsToExportBlocks(revision.studentSubmissionOps ?? []);
    await appendOpsBlocksToPDFContent(composition.stack, blocks);

    if (revisionCommentPlacement == RevisionCommentPlacement.AfterDocument) {
      appendCommentsToPDFContent(composition.stack, revision, commentColor);
    }

    appendAnnotationSummary(composition.stack, annotations, commentColor);

    const docDefinition: any = {
      pageMargins: [60, 60, 60, 60],
      // pdfmake lays out content and populates node positions before headers.
      // Read the actual start page so multi-page checklists stay unnumbered too.
      header: buildHeader(revision, () => composition.positions?.[0]?.pageNumber ?? 1),
      content,
      defaultStyle: {
        font: "Tinos",
      },
    };

    const pdfMake = usePDFMake();
    pdfMake.fonts = {
      Tinos: {
        normal: window.location.origin + "/fonts/tinos/Tinos-Regular.ttf",
        bold: window.location.origin + "/fonts/tinos/Tinos-Bold.ttf",
        italics: window.location.origin + "/fonts/tinos/Tinos-Italic.ttf",
        bolditalics:
          window.location.origin + "/fonts/tinos/Tinos-BoldItalic.ttf",
      },
      Roboto: {
        normal: window.location.origin + "/fonts/roboto/Roboto-Regular.ttf",
        bold: window.location.origin + "/fonts/roboto/Roboto-Bold.ttf",
        italics: window.location.origin + "/fonts/roboto/Roboto-Italic.ttf",
        bolditalics:
          window.location.origin + "/fonts/roboto/Roboto-BoldItalic.ttf",
      },
    };

    if (returnBlob) {
      return new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => resolve(blob));
      });
    }

    if (printMode) {
      pdfMake.createPdf(docDefinition).print();
    } else {
      pdfMake.createPdf(docDefinition).download(docName);
    }
  };

  return { exportPDFOpsDocument };
};
